import { useEffect, useRef } from 'react';
import SignaturePadLib from 'signature_pad';
import { Button } from './ui/Button';

interface Props {
  title: string;
  onConfirm: (png: string) => void;
  initialValue?: string | null;
}

export function SignaturePad({ title, onConfirm, initialValue }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  const onConfirmRef = useRef(onConfirm);

  useEffect(() => {
    onConfirmRef.current = onConfirm;
  }, [onConfirm]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    const applyScale = (): void => {
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(ratio, ratio);
    };

    applyScale();

    const pad = new SignaturePadLib(canvas, {
      backgroundColor: 'rgb(255,255,255)',
      penColor: 'rgb(10,20,40)',
      minWidth: 0.8,
      maxWidth: 2.4,
    });

    const saveSignature = (): void => {
      if (pad.isEmpty()) {
        onConfirmRef.current('');
        return;
      }
      onConfirmRef.current(pad.toDataURL('image/png'));
    };

    pad.addEventListener('endStroke', saveSignature);
    padRef.current = pad;

    if (initialValue) {
      void pad.fromDataURL(initialValue);
    }

    const handleResize = (): void => {
      const data = pad.toData();
      applyScale();
      pad.clear();
      if (data.length) {
        pad.fromData(data);
        saveSignature();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      pad.off();
    };
  // initialValue намеренно не в зависимостях: компонент монтируется с key={current.key},
  // поэтому при смене документа происходит полный ремаунт с нужным значением.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clear = (): void => {
    padRef.current?.clear();
    onConfirm('');
  };

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm text-slate-600">Распишитесь в поле ниже — стилусом или пальцем.</p>
      <div className="border-2 border-dashed border-slate-400 rounded-xl bg-white">
        <canvas
          ref={canvasRef}
          className="w-full"
          style={{ height: 260, touchAction: 'none' }}
        />
      </div>
      <div className="flex gap-3">
        <Button type="button" variant="secondary" onClick={clear}>
          Очистить
        </Button>
      </div>
    </div>
  );
}
